import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import CalEventEditState from '../Types/CalEventEditState';
import { useDraggableDialog } from '../useDraggableDialog';
import { useReminderEditor } from '../useReminderEditor';
import { useCalEventEdit } from '../useCalEventEdit';
import calendarEngine from '../Core/CalendarEngine';
import CalEventFields from './CalEventFields';
import './EditEventDialog.css';

const DEFAULT_POS = { top: 160, left: window.innerWidth / 2 - 160 };

/**
 * Dialog for editing a CalEvent — label, date, time, cat, description and reminder offset.
 * Self-contained — reads editingRk/newEventDraft via useCalEventEdit (pub/sub),
 * so it only repaints on edit-state changes, not on every CalendarWidget repaint.
 * No props needed — render once as a sibling of CalendarWidgetContent.
 *
 * Keyboard: Enter saves (canSave permitting). Inside the multiline description
 * textarea, plain Enter inserts a newline — Ctrl/Cmd+Enter saves instead.
 * Escape cancels.
 */
export default function EditEventDialog() {
    const { editingRk, newEventDraft, endEdit } = useCalEventEdit();

    const isNewEvent = editingRk === '__new__';
    const event = isNewEvent ? newEventDraft : (calendarEngine.getUpcoming().find((e) => e.rk === editingRk) ?? null);
    const open = !!event;

    const { pos, dragging, titlebarProps, dialogProps } = useDraggableDialog(DEFAULT_POS);
    const reminderEditor = useReminderEditor(event?.remind, open);

    // Snapshot of event at open time — single source of truth for defaults
    const snapshot = new CalEventEditState(event);

    const [label, setLabel] = useState(snapshot.label);
    const [date, setDate] = useState(snapshot.date);
    const [time, setTime] = useState(snapshot.time);
    const [cat, setCat] = useState(snapshot.cat);
    const [desc, setDesc] = useState(snapshot.desc);

    // Re-sync form state when a new event is opened
    useEffect(() => {
        if (!open || !event) return;
        const fresh = new CalEventEditState(event);
        setLabel(fresh.label);
        setDate(fresh.date);
        setTime(fresh.time);
        setCat(fresh.cat);
        setDesc(fresh.desc);
    }, [open, event]);

    if (!open || !event) return null;

    // Current state — compared against snapshot for isDirty
    const current = new CalEventEditState({
        label,
        date,
        time,
        cat,
        desc,
        remind: reminderEditor.activeValue,
    });
    const isDirty = snapshot.isDirty(current);
    const canSave = isDirty && reminderEditor.isValid;

    /**
     * Generic field change handler — maps field name to its setter.
     * @param {string} field
     * @param {string} value
     */
    const handleChange = (field, value) => {
        const setters = { label: setLabel, date: setDate, time: setTime, cat: setCat, desc: setDesc };
        setters[field]?.(value);
    };

    const handleSave = () => {
        if (!canSave) return;

        calendarEngine.updateEvent(event, {
            label,
            date,
            time,
            cat,
            desc,
            remind: reminderEditor.activeValue,
        });
        endEdit();
    };

    /**
     * Enter saves. In the multiline description textarea, plain Enter inserts
     * a newline instead — Ctrl/Cmd+Enter saves there. Escape cancels.
     * @param {React.KeyboardEvent} e
     */
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            endEdit();
            return;
        }

        if (e.key !== 'Enter') return;

        const inTextarea = e.target.tagName === 'TEXTAREA';

        if (inTextarea && !(e.ctrlKey || e.metaKey)) {
            // plain Enter in description — let it insert a newline
            return;
        }

        e.preventDefault();
        handleSave();
    };

    return createPortal(
        <div className="ce-dialog" style={{ top: pos.top, left: pos.left }} {...dialogProps} onKeyDown={handleKeyDown}>
            {/* Titlebar */}
            <div className="ce-titlebar" {...titlebarProps}>
                <EditIcon sx={{ fontSize: 13, color: '#a3d1ff', mr: '6px' }} />
                <span>Edit Event</span>
                <div className="ce-titlebar-actions">
                    <CheckIcon
                        sx={{ fontSize: 16, cursor: canSave ? 'pointer' : 'default', color: canSave ? '#4caf8a' : '#294766' }}
                        onClick={handleSave}
                    />
                    <CloseIcon sx={{ fontSize: 16, cursor: 'pointer', color: '#a3d1ff' }} onClick={endEdit} />
                </div>
            </div>

            <div className="ce-body">
                <CalEventFields current={current} onChange={handleChange} reminderEditor={reminderEditor} />
            </div>

            <div className="ce-footer">
                <button className="ce-btn ce-btn-cancel" onClick={endEdit}>
                    Cancel
                </button>
                <button className={`ce-btn ce-btn-save${canSave ? ' ready' : ''}`} onClick={handleSave} disabled={!canSave}>
                    Save
                </button>
            </div>
        </div>,
        document.body,
    );
}
